import React, { useState, useCallback, useEffect, type ReactElement } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { token } from '@atlaskit/tokens';
import PageIcon from '@atlaskit/icon/core/page';

import {
	CommentType,
	type CommentData,
	type ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { OPTIMISTIC_SAVE_COMMENT_ID } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';
import { commentsi18n } from '@confluence/comments-util/entry-points/commentsi18n.ts';
import { formatTimestampForAriaLabel } from '@confluence/comments-util/entry-points/dateUtils.ts';
import { useGetCommentStatus } from '@confluence/comments-util/entry-points/commentStatus.ts';
import { useCommentsPanelState } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type {
	CommentLocation,
	GeneralCommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { PageInfoNode } from '@confluence/page-info/entry-points/PageInfoQuery.ts';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useUnreadCommentsState } from '@confluence/unread-comments/entry-points/useUnreadComments.ts';

import { getShouldShowRepliesSection } from '../helpers/repliesHelpers.ts';

// TODO COMMENTS-5941: Use Pick from CommentRepliesProps after moving CommentReplies component into comments-common
export type CommentComponentProps = {
	comment: CommentData | ReplyData;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	isUnread: boolean;
	threadKey: string;
	hideBranchingStyle: boolean;
	canAddComments: boolean;
};

// TODO COMMENTS-5941: Use Pick from CommentProps after moving Comment component into comments-common
export type CommentRepliesComponentProps = {
	parentComment: CommentData;
	pageInfo: PageInfoNode | null;
	pageMode: PageMode;
	parentCommentId: string;
	canAddComments: boolean;
	isEditorExpanded: boolean;
};

export type BaseCommentThreadProps = {
	commentThreadRef?: React.RefObject<HTMLDivElement>;
	commentComponent: (commentComponentProps: CommentComponentProps) => ReactElement;
	commentRepliesComponent: (
		commentRepliesComponentProps: CommentRepliesComponentProps,
	) => ReactElement;
	parentComment: CommentData & {
		isFirstPageComment?: boolean;
	};
	pageMode: PageMode;
	shouldRenderCommentTypeSeparator?: boolean;
	canAddComments: boolean;
	handleCommentThreadClick: (event: React.MouseEvent) => void;
	handleCommentThreadMouseDown: () => void;
	testIdPrefix?: string;
	isEditorExpanded: boolean;
	/** When true, always render the replies section (e.g. comment summary popup needs the Reply button even with 0 replies) */
	forceShowRepliesSection?: boolean;
	/**
	 * When true, the parent comment has an associated agent session, so the branch/thread
	 * line is kept visible even when the comment has no replies (the line connects the
	 * comment to its agent-status row). Surface-agnostic boolean — callers compute it.
	 */
	parentHasAgentSession?: boolean;
};

const styles = cssMap({
	threadBoxStyles: {
		cursor: 'pointer',
		listStyle: 'none',
		position: 'relative',
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'), // TODO COMMENTS-5910: override to space.250 for ObjectCommentThread
		paddingTop: token('space.200'), // TODO COMMENTS-5910: override to space.100 for ObjectCommentThread
		paddingBottom: token('space.200'),
		margin: '0',
		display: 'flex',
		flexDirection: 'column',
		// TODO COMMENTS-5910: Add &:focus and &:focus-visible stiles for ObjectCommentThread
	},
	selectedThreadStyles: {
		backgroundColor: token('color.background.input.hovered'),
	},
	commentTypeSeperatorStyle: {
		gap: token('space.050'),
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`, // TODO COMMENTS-5910: no borderTop for ObjectCommentThread
	},
	commentBodyBoxStyles: {
		listStyle: 'none',
		position: 'relative',
		// TODO COMMENTS-5910: override marginRight: token('space.100') for ObjectCommentThread
	},
});

const i18n = defineMessages({
	commentTypeSeperatorText: {
		id: 'comments-common.comment.thread.type-seperator',
		defaultMessage: 'General comments',
		description: 'Text for the seperator of inline and general comment section',
	},
	threadContentAriaLabelWithName: {
		// COMMENTS-5979: ObjectCommentThread doesn't have timestamp, but it probably should
		id: 'comments-common.thread.content.aria-label.with-name',
		defaultMessage: '{name}’s comment thread, {timestamp}',
		description:
			'Aria-label for content associated with the comment thread, with the first comment author name and timestamp',
	},
});

// This component renders the parent comment and its replies.
// It also handles UI states, like hover, pressed, etc. and checks add comments permissions
export const BaseCommentThread = ({
	commentThreadRef,
	commentComponent,
	commentRepliesComponent,
	parentComment,
	pageMode,
	shouldRenderCommentTypeSeparator,
	canAddComments,
	handleCommentThreadClick,
	handleCommentThreadMouseDown: onCommentThreadMouseDown,
	testIdPrefix,
	isEditorExpanded,
	forceShowRepliesSection = false,
	parentHasAgentSession = false,
}: BaseCommentThreadProps): React.JSX.Element => {
	const intl = useIntl();
	const { formatMessage } = intl;

	const { currentlySelectedThreadKey, isCurrentlySelectedThreadOptimisticallyAdded } =
		useCommentsPanelState();
	const { pageInfo } = usePageInfo();
	const { unreadCommentIDSet } = useUnreadCommentsState();

	const threadKey =
		parentComment.type === CommentType.INLINE
			? ((parentComment.location as CommentLocation).inlineMarkerRef ?? '')
			: parentComment.id;

	const [isThreadPressed, setIsThreadPressed] = useState<boolean>(false);

	const isCurrentSelectedComment =
		currentlySelectedThreadKey === threadKey ||
		// Special handling to avoid UI flicker when optimistically adding a footer comment.
		// See comments in `next/packages/comments-panel-utils/src/useCommentsPanel.ts` for details.
		(parentComment.type === CommentType.GENERAL &&
			!!isCurrentlySelectedThreadOptimisticallyAdded &&
			threadKey === OPTIMISTIC_SAVE_COMMENT_ID);

	useEffect(() => {
		const parentIdOfSelectedReply = parentComment?.replies?.find(
			(reply) => reply.id === currentlySelectedThreadKey,
		);
		if (isCurrentSelectedComment || parentIdOfSelectedReply) {
			setIsThreadPressed(true);
		} else {
			setIsThreadPressed(false);
		}
	}, [currentlySelectedThreadKey, isCurrentSelectedComment, parentComment?.replies]);

	const handleCommentThreadMouseDown = useCallback(() => {
		onCommentThreadMouseDown?.();

		/**
		 * Limiting executions of this function because updating isThreadPressed
		 * rerenders every component in this tree. Most notably, a user clicking into
		 * the reply comment editor will call this function every time (and really any sibling component).
		 * 1. isThreadPressed is only used as a style variable alongside isCurrentSelectedComment
		 * 2. if clicking doesn't change threadKey then currentlySelectedThreadKey never changes either
		 * which are the two variables isCurrentSelectedComment compares. We should only call this function
		 * if threadKey has changed.
		 */
		if (!isCurrentSelectedComment) {
			setIsThreadPressed(true);
		}
	}, [onCommentThreadMouseDown, isCurrentSelectedComment]);

	const resolveProperties =
		(parentComment.location as CommentLocation)?.inlineResolveProperties ||
		(parentComment.location as GeneralCommentLocation)?.commentResolveProperties ||
		undefined;

	const commentStatus = useGetCommentStatus({
		resolveProperties,
	});

	const timestamp = formatTimestampForAriaLabel({
		intl,
		dateString: parentComment.createdAtNonLocalized,
		commentStatus,
		resolveProperties,
	});

	const testIdPrefixWithDelimiter = testIdPrefix ? `${testIdPrefix}-` : '';

	return (
		<>
			{shouldRenderCommentTypeSeparator && (
				<Flex
					xcss={styles.commentTypeSeperatorStyle}
					as="li"
					testId="comment-section-separator"
					alignItems="center"
				>
					<PageIcon
						testId="general-comment-page-icon"
						color={token('color.icon.subtlest')}
						label={formatMessage(i18n.commentTypeSeperatorText)}
					/>
					<Text color="color.text.subtlest" weight="medium">
						{formatMessage(i18n.commentTypeSeperatorText)}
					</Text>
				</Flex>
			)}
			<Box
				ref={commentThreadRef}
				id={`${testIdPrefixWithDelimiter}comment-thread-${threadKey}-container`}
				testId={`${testIdPrefixWithDelimiter}comment-thread-${threadKey}-container`}
				xcss={cx(
					styles.threadBoxStyles,
					(isCurrentSelectedComment || isThreadPressed) && styles.selectedThreadStyles,
				)}
				data-selected-comment-thread={
					isCurrentSelectedComment && (parentComment.location as CommentLocation)?.inlineMarkerRef
						? (parentComment.location as CommentLocation).inlineMarkerRef
						: undefined
				}
				onClick={handleCommentThreadClick}
				onMouseDown={handleCommentThreadMouseDown}
				onMouseUp={() => setIsThreadPressed(false)}
				onKeyDown={() => {}} // TODO COMMENTS-5943: Implement actual onKeyDown handler
				tabIndex={0}
				aria-label={formatMessage(i18n.threadContentAriaLabelWithName, {
					name: parentComment.author?.displayName || formatMessage(commentsi18n.anonymous),
					timestamp,
				})}
				role="button"
			>
				<Box
					testId={`${testIdPrefixWithDelimiter}comment-thread-${parentComment.id}-parent`}
					xcss={styles.commentBodyBoxStyles}
				>
					{commentComponent({
						comment: parentComment,
						pageInfo,
						pageMode,
						isUnread: unreadCommentIDSet.has(parentComment.id),
						threadKey,
						hideBranchingStyle:
							!parentHasAgentSession &&
							(expValEquals('confluence_comments_tuneup', 'isEnabled', true)
								? parentComment.replies.length === 0 && !isEditorExpanded
								: (!parentComment.isOpen || !canAddComments) && parentComment.replies.length === 0),
						canAddComments,
					})}
				</Box>
				{(getShouldShowRepliesSection({
					replies: parentComment.replies,
					isEditorExpanded,
				}) ||
					forceShowRepliesSection) &&
					commentRepliesComponent({
						parentComment,
						pageInfo,
						pageMode,
						parentCommentId: parentComment.id,
						canAddComments,
						isEditorExpanded,
					})}
			</Box>
		</>
	);
};
